import React, { useEffect, useState } from 'react';
import AmenitiesBookingModal from './AmenitiesBookingModal';
import { useToken, useGuest } from '../App';
import { API_ENDPOINTS } from '../config/api';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useHotelTitle } from '../hooks/useHotelTitle';

const GuestExplore: React.FC = () => {
  const { hotelId, token } = useToken()!;
  const guest = useGuest();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hotelName } = useHotelTitle('Amenities');
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAmenity, setSelectedAmenity] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [myBookings, setMyBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsDate, setBookingsDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    setError('');
    fetch(API_ENDPOINTS.AMENITIES(hotelId))
      .then(res => res.json())
      .then(data => {
        const amenitiesData = Array.isArray(data) ? data : [];
        setAmenities(amenitiesData);
        setLoading(false);
        
        // Check if there's an amenity parameter in the URL
        const amenityId = searchParams.get('amenity');
        if (amenityId && amenitiesData.length > 0) {
          const amenity = amenitiesData.find(a => a.id.toString() === amenityId);
          if (amenity) {
            setSelectedAmenity(amenity);
          }
        }
      })
      .catch(() => {
        setError('Failed to load amenities. Please try again.');
        setLoading(false);
      });
  }, [hotelId, searchParams]);

  const handleAmenityClick = (amenity: any) => {
    // Navigate to the specific amenity detail view
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('amenity', amenity.id.toString());
    setSearchParams(newSearchParams);
  };

  const handleBookNowClick = (amenity: any) => {
    setSelectedAmenity(amenity);
    setShowModal(true);
  };

  const handleBackToAllAmenities = () => {
    setSelectedAmenity(null);
    // Clear the amenity parameter from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('amenity');
    setSearchParams(newSearchParams);
  };

  const fetchBookings = (date?: string) => {
    if (!hotelId || !guest?.guest_id) return;
    setLoadingBookings(true);
    let url = API_ENDPOINTS.AMENITIES_GUEST_BOOKINGS(hotelId, guest.guest_id);
    if (date) url += `?date=${date}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        setMyBookings(Array.isArray(data) ? data : []);
        setLoadingBookings(false);
      })
      .catch(() => {
        setMyBookings([]);
        setLoadingBookings(false);
      });
  };

  const handleShowBookings = () => {
    setShowBookings(true);
    fetchBookings(bookingsDate);
  };

  const handleBookingsDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBookingsDate(e.target.value);
    fetchBookings(e.target.value);
  };

  return (
    <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
      {/* Show selected amenity if there's a URL parameter */}
      {selectedAmenity ? (
        <div>
          {/* Back button */}
          <div className="mb-3">
            <button 
              className="btn"
              onClick={handleBackToAllAmenities}
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 8,
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '8px 16px',
                color: '#6c757d',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                minWidth: 'auto',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e9ecef';
                e.currentTarget.style.borderColor = '#dee2e6';
                e.currentTarget.style.color = '#495057';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.color = '#6c757d';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
              }}
            >
              <i className="fas fa-chevron-left" style={{ fontSize: '12px' }}></i>
              Back
            </button>
          </div>
          
          {/* Selected amenity details */}
          <div className="card shadow-sm border-0">
            {selectedAmenity.image_url && (
              <img 
                src={selectedAmenity.image_url.startsWith('http') ? selectedAmenity.image_url : API_ENDPOINTS.IMAGE_BASE + (selectedAmenity.image_url.startsWith('public/') ? selectedAmenity.image_url : 'public/' + selectedAmenity.image_url)} 
                alt={selectedAmenity.name} 
                className="card-img-top" 
                style={{ height: 200, objectFit: 'cover' }} 
              />
            )}
            <div className="card-body">
              <h3 className="card-title fw-bold mb-2">{selectedAmenity.name}</h3>
              <p className="card-text text-muted mb-3">{selectedAmenity.description}</p>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className={`badge ${selectedAmenity.is_active ? 'bg-success' : 'bg-secondary'}`}>
                  {selectedAmenity.is_active ? 'Available' : 'Unavailable'}
                </span>
              </div>
              <button 
                className="btn btn-primary w-100" 
                onClick={() => handleBookNowClick(selectedAmenity)}
                disabled={!selectedAmenity.is_active}
              >
                <i className="fas fa-calendar-check me-2"></i>
                Book Now
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Show all amenities if no specific amenity is selected */
        <div>
          <h2 className="section-title mb-3" style={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 22, color: '#4a6cf7' }}><i className="fas fa-swimming-pool"></i></span>
            Hotel Amenities
          </h2>
          {loading ? (
            <div className="text-center py-5"><span className="spinner-border" /> Loading amenities...</div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : amenities.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fas fa-spa fa-3x mb-3 text-primary"></i>
              <h4>No amenities available</h4>
              <p>Check back later for new experiences!</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}>
              {amenities.map(amenity => (
                <div key={amenity.id}>
                  <div className="card shadow-sm border-0 mb-2 amenity-card h-100" style={{ cursor: 'pointer', transition: 'transform 0.13s', minHeight: 120, borderRadius: 10, padding: 0 }} onClick={() => handleAmenityClick(amenity)}>
                    {amenity.image_url && (
                      <img src={amenity.image_url.startsWith('http') ? amenity.image_url : API_ENDPOINTS.IMAGE_BASE + (amenity.image_url.startsWith('public/') ? amenity.image_url : 'public/' + amenity.image_url)} alt={amenity.name} className="card-img-top" style={{ height: 70, objectFit: 'cover', borderTopLeftRadius: 10, borderTopRightRadius: 10 }} />
                    )}
                    <div className="card-body d-flex flex-column justify-content-between p-2" style={{ minHeight: 60 }}>
                      <div>
                        <div className="fw-bold" style={{ fontSize: 15 }}>{amenity.name}</div>
                        <div 
                          className="text-muted small mb-1" 
                          style={{ 
                            fontSize: 12,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.2'
                          }}
                        >
                          {amenity.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <AmenitiesBookingModal
        show={showModal}
        onHide={() => setShowModal(false)}
        amenity={selectedAmenity}
        hotelId={hotelId || ''}
        guestId={guest?.guest_id || ''}
        onBookingSuccess={() => {}}
      />
      {/* Floating My Bookings Button */}
      {!showBookings && (
        <button
          className="guest-bookings-fab"
          onClick={handleShowBookings}
          title="View My Amenity Bookings"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 80,
            zIndex: 2001,
            background: '#4a6cf7',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px rgba(74,108,247,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
            cursor: 'pointer',
          }}
        >
          <i className="fas fa-list"></i>
        </button>
      )}
      {/* My Amenity Bookings Modal */}
      <Modal show={showBookings} onHide={() => setShowBookings(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>My Amenity Bookings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div className="mb-3 d-flex align-items-center gap-2">
              <label htmlFor="my-bookings-date" className="mb-0">Date:</label>
              <input
                type="date"
                id="my-bookings-date"
                className="form-control"
                style={{ width: 160 }}
                value={bookingsDate}
                onChange={handleBookingsDateChange}
                max="9999-12-31"
              />
            </div>
            {loadingBookings ? (
              <div className="text-center py-4"><span className="spinner-border" /> Loading your bookings...</div>
            ) : myBookings.length === 0 ? (
              <div className="text-center text-muted py-4">No bookings found.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Amenity</th>
                      <th>Date</th>
                      <th>Slot Time</th>
                      <th>Pricing</th>
                      <th>Status</th>
                      <th>Special Request</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myBookings.map((b, i) => (
                      <tr key={b.id || i}>
                        <td>{b.amenity_name}</td>
                        <td>{b.booking_date}</td>
                        <td>{b.slot_time}</td>
                        <td>{b.price ? `₹${b.price}` : '-'}</td>
                        <td>
                          {b.status === 'confirmed' ? (
                            <span className="badge bg-success">Confirmed</span>
                          ) : b.status === 'pending' ? (
                            <span className="badge bg-warning text-dark">Pending</span>
                          ) : (
                            <span className="badge bg-secondary">Cancelled</span>
                          )}
                        </td>
                        <td>{b.special_request}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBookings(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GuestExplore; 